import { InputAdornment, Stack, Typography, useTheme } from '@mui/material'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import cs from 'date-fns/locale/cs'
import React from 'react'
import CustomTextField from './CustomTextField'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'

interface DateTimeRangePickerProps {
    onSetDateTo: (dateTo: Date | null) => void
    onSetDateFrom: (dateFrom: Date | null) => void
    dateTo: string
    dateFrom: string
    labelTo: string
    labelFrom: string
    /**
     * If true, the picker will have cross icon to cancel selection.
     */
    canBeCanceled?: boolean
}

const DateTimeRangePicker: React.FC<DateTimeRangePickerProps> = ({
    onSetDateFrom,
    onSetDateTo,
    dateFrom,
    dateTo,
    labelFrom,
    labelTo,
    canBeCanceled = true,
}: DateTimeRangePickerProps) => {
    const [openFrom, setOpenFrom] = React.useState(false)
    const [openTo, setOpenTo] = React.useState(false)
    const theme = useTheme()

    return (
        <Stack spacing={theme.spacing(2)} direction='row' style={{ maxWidth: 450, minWidth: 300 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={cs}>
                <DateTimePicker
                    label={labelFrom}
                    value={dateFrom}
                    open={openFrom}
                    onOpen={() => setOpenFrom(true)}
                    onClose={() => setOpenFrom(false)}
                    onChange={(newValue: Date | null) => {
                        if (typeof newValue !== 'object') return
                        onSetDateFrom(newValue)
                    }}
                    renderInput={(params) => (
                        <CustomTextField
                            {...params}
                            size='small'
                            onClick={() => {
                                setOpenFrom(true)
                                setOpenTo(false)
                            }}
                            value={new Date(dateFrom || new Date()).toLocaleString('cs', {
                                year: 'numeric',
                                month: 'numeric',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                            })}
                            inputProps={{
                                style: {
                                    minWidth: 150,
                                    paddingLeft: 7,
                                },
                            }}
                        />
                    )}
                    components={{
                        OpenPickerIcon: () =>
                            canBeCanceled ? (
                                <div
                                    onClick={() => onSetDateFrom(null)}
                                    style={{ display: dateFrom ? '' : 'none', marginTop: theme.spacing(1) }}
                                >
                                    {<CloseOutlinedIcon />}
                                </div>
                            ) : null,
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position='end'>
                                <CloseOutlinedIcon />
                            </InputAdornment>
                        ),
                        style: {
                            maxHeight: 36,
                        },
                    }}
                />
                <Typography variant='subtitle1' pt={1}>
                    -
                </Typography>
                <DateTimePicker
                    label={labelTo}
                    value={dateTo}
                    onOpen={() => setOpenTo(true)}
                    onClose={() => setOpenTo(false)}
                    open={openTo}
                    onChange={(newValue) => {
                        if (typeof newValue !== 'object') return
                        onSetDateTo(newValue)
                    }}
                    renderInput={(params) => (
                        <CustomTextField
                            {...params}
                            size='small'
                            onClick={() => {
                                setOpenTo(true)
                                setOpenFrom(false)
                            }}
                            value={new Date(dateTo || new Date()).toLocaleString('cs', {
                                year: 'numeric',
                                month: 'numeric',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                            })}
                            inputProps={{
                                style: {
                                    minWidth: 150,
                                    paddingLeft: 7,
                                },
                            }}
                        />
                    )}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position='end'>
                                <CloseOutlinedIcon />
                            </InputAdornment>
                        ),
                        style: {
                            maxHeight: 36,
                        },
                    }}
                    components={{
                        OpenPickerIcon: () =>
                            canBeCanceled ? (
                                <div
                                    onClick={() => onSetDateTo(null)}
                                    style={{ display: dateTo ? '' : 'none', marginTop: theme.spacing(1) }}
                                >
                                    <CloseOutlinedIcon />
                                </div>
                            ) : null,
                    }}
                />
            </LocalizationProvider>
        </Stack>
    )
}

export default DateTimeRangePicker
